import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useGuidesBase } from '../hooks/useGuidesBase';
import {
  Box, Title, Paper, Text, Stack, Group, Stepper, Code, Tabs,
  Alert, Badge, Anchor, ThemeIcon, CopyButton, ActionIcon, Tooltip,
} from '@mantine/core';
import { Target, Globe, ShoppingCart, Phone, Check, Copy, Warning, Lightbulb } from '@phosphor-icons/react';
import { Breadcrumbs } from '@mantine/core';
import SEO from '../components/SEO';

const GTAG_SNIPPET = `<!-- Google tag (gtag.js) -->
<script async src="https://www.googletagmanager.com/gtag/js?id=AW-XXXXXXXXXX"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', 'AW-XXXXXXXXXX');
</script>`;

const CONVERSION_SNIPPET = `gtag('event', 'conversion', {
  'send_to': 'AW-XXXXXXXXXX/YYYYYYYYYYY',
  'value': 100.0,
  'currency': 'PLN'
});`;

const GTM_DATALAYER = `// Na stronie podziękowania / potwierdzenia zamówienia:
dataLayer.push({
  'event': 'purchase',
  'value': 150.00,
  'currency': 'PLN',
  'transaction_id': 'ORDER-12345'
});`;

function CopyBlock({ code }) {
  return (
    <Box style={{ position: 'relative' }}>
      <Code block style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{code}</Code>
      <CopyButton value={code}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? 'Skopiowano' : 'Kopiuj'} position="left">
            <ActionIcon
              variant="subtle"
              color={copied ? 'green' : 'gray'}
              onClick={copy}
              style={{ position: 'absolute', top: 8, right: 8 }}
              size="sm"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Box>
  );
}

function ConversionType({ icon, title, description, steps }) {
  const Icon = icon;
  return (
    <Paper withBorder p="md" radius="md">
      <Group gap="sm" mb="sm">
        <ThemeIcon variant="light" color="brand" size="md" radius="md">
          <Icon size={18} weight="bold" />
        </ThemeIcon>
        <Box>
          <Text size="sm" fw={600}>{title}</Text>
          <Text size="xs" c="dimmed">{description}</Text>
        </Box>
      </Group>
      <Stack gap="xs">
        {steps.map((step, i) => (
          <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
            <Badge size="xs" color="brand" variant="filled" circle>{i + 1}</Badge>
            <Text size="sm" lh={1.5}>{step}</Text>
          </Group>
        ))}
      </Stack>
    </Paper>
  );
}

export default function ConversionGuide() {
  const [active, setActive] = useState(0);
  const guidesBase = useGuidesBase();

  return (
    <Box px="lg" py="md">
      <SEO
        title="Śledzenie konwersji w Google Ads"
        description="Jak skonfigurować śledzenie konwersji w Google Ads. Google Tag, zdarzenia konwersji, import z Analytics. Niezbędne do optymalizacji kampanii."
        path={`${guidesBase}/conversions`}
        noIndex={guidesBase !== '/poradniki'}
      />

      <Breadcrumbs mb="sm" styles={{ separator: { color: 'var(--mantine-color-gray-4)' } }}>
        <Anchor component={Link} to={guidesBase} size="sm">Przewodniki</Anchor>
        <Text size="sm" c="dark">Śledzenie konwersji</Text>
      </Breadcrumbs>
      <Title order={3} mb="md">Śledzenie konwersji</Title>

      <Alert color="blue" mb="md" icon={<Lightbulb size={16} />}>
        <Text size="sm">
          Bez śledzenia konwersji agent AI nie może optymalizować kampanii pod kątem sprzedaży.
          Skonfiguruj konwersje, aby Droid mógł automatycznie zwiększać ROAS.
        </Text>
      </Alert>

      {/* Step-by-step guide */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="md">Konfiguracja krok po kroku</Text>
        <Stepper active={active} onStepClick={setActive} size="sm" color="brand">
          <Stepper.Step label="Utwórz konwersję" description="W Google Ads">
            <Stack gap="sm" mt="md">
              <Text size="sm" lh={1.6}>
                1. Zaloguj się do Google Ads i przejdź do <strong>Cele {'>'} Konwersje {'>'} Podsumowanie</strong>
              </Text>
              <Text size="sm" lh={1.6}>
                2. Kliknij <strong>+ Nowe działanie powodujące konwersję</strong>
              </Text>
              <Text size="sm" lh={1.6}>
                3. Wybierz <strong>Witryna</strong> jako źródło konwersji
              </Text>
              <Text size="sm" lh={1.6}>
                4. Wypełnij formularz:
              </Text>
              <Paper withBorder p="sm" radius="sm" ml="md">
                <Stack gap={4}>
                  <Group gap="xs"><Text size="xs" fw={500} w={120}>Kategoria:</Text><Text size="xs">Zakup / Lead / Inny</Text></Group>
                  <Group gap="xs"><Text size="xs" fw={500} w={120}>Nazwa:</Text><Text size="xs">np. "Zakup", "Formularz kontaktowy"</Text></Group>
                  <Group gap="xs"><Text size="xs" fw={500} w={120}>Wartość:</Text><Text size="xs">Stała lub dynamiczna (z kodu)</Text></Group>
                  <Group gap="xs"><Text size="xs" fw={500} w={120}>Zliczanie:</Text><Text size="xs">"Każda" dla zakupów, "Jedna" dla leadów</Text></Group>
                </Stack>
              </Paper>
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Zainstaluj tag" description="Na stronie">
            <Stack gap="sm" mt="md">
              <Tabs defaultValue="gtag">
                <Tabs.List>
                  <Tabs.Tab value="gtag">Tag globalny (gtag.js)</Tabs.Tab>
                  <Tabs.Tab value="gtm">Google Tag Manager</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="gtag" pt="sm">
                  <Text size="sm" mb="sm">
                    Dodaj ten kod w sekcji <Code>{'<head>'}</Code> na każdej stronie:
                  </Text>
                  <CopyBlock code={GTAG_SNIPPET} />
                  <Alert color="yellow" mt="sm" icon={<Warning size={14} />}>
                    <Text size="xs">Zamień <Code>AW-XXXXXXXXXX</Code> na swój ID konwersji z Google Ads.</Text>
                  </Alert>
                </Tabs.Panel>

                <Tabs.Panel value="gtm" pt="sm">
                  <Text size="sm" mb="sm">
                    Jeśli korzystasz z GTM, dodaj tag "Google Ads Conversion Tracking" w kontenerze.
                    Na stronie podziękowania wypchnij zdarzenie:
                  </Text>
                  <CopyBlock code={GTM_DATALAYER} />
                </Tabs.Panel>
              </Tabs>
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Dodaj kod konwersji" description="Na stronie podziękowania">
            <Stack gap="sm" mt="md">
              <Text size="sm" lh={1.6}>
                Na stronie potwierdzenia zamówienia lub podziękowania za kontakt dodaj:
              </Text>
              <CopyBlock code={CONVERSION_SNIPPET} />
              <Text size="xs" c="dimmed">
                Zamień <Code>AW-XXXXXXXXXX/YYYYYYYYYYY</Code> na ID z Google Ads
                (znajdziesz go w ustawieniach konwersji).
              </Text>
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Zweryfikuj" description="Sprawdź działanie">
            <Stack gap="sm" mt="md">
              <Text size="sm" lh={1.6}>
                1. Zainstaluj <Anchor href="https://chrome.google.com/webstore/detail/google-tag-assistant/kejbdjndbnbjgmefkgdddjlbokphdefk" target="_blank" size="sm">Google Tag Assistant</Anchor> w Chrome
              </Text>
              <Text size="sm" lh={1.6}>
                2. Otwórz swoją stronę i sprawdź, czy tag Google Ads jest wykryty
              </Text>
              <Text size="sm" lh={1.6}>
                3. Wykonaj testową konwersję (zamówienie testowe lub formularz)
              </Text>
              <Text size="sm" lh={1.6}>
                4. W Google Ads przejdź do <strong>Cele {'>'} Konwersje</strong> i sprawdź, czy pojawił się status "Rejestrowanie konwersji"
              </Text>
              <Alert color="green" icon={<Check size={14} />}>
                <Text size="xs">
                  Konwersje mogą pojawić się w Google Ads z opóźnieniem do 24 godzin.
                  Po weryfikacji Droid automatycznie zacznie optymalizować pod kątem konwersji.
                </Text>
              </Alert>
            </Stack>
          </Stepper.Step>
        </Stepper>
      </Paper>

      {/* Conversion types */}
      <Text size="sm" fw={600} mb="sm">Popularne typy konwersji</Text>
      <Stack gap="sm">
        <ConversionType
          icon={ShoppingCart}
          title="Zakup w sklepie internetowym"
          description="E-commerce: WooCommerce, Shopify, PrestaShop"
          steps={[
            'Ustaw kategorię: "Zakup"',
            'Wartość: dynamiczna (z kodu sklepu)',
            'Zliczanie: "Każda konwersja"',
            'Większość platform ma wtyczki do Google Ads (np. WooCommerce Google Ads Conversion Tracking)',
          ]}
        />
        <ConversionType
          icon={Phone}
          title="Formularz kontaktowy / Lead"
          description="Strony usługowe, landing page"
          steps={[
            'Ustaw kategorię: "Przesłanie formularza kontaktowego"',
            'Wartość: stała (np. średnia wartość leada)',
            'Zliczanie: "Jedna konwersja" (unikaj duplikatów)',
            'Kod konwersji na stronie "Dziękujemy" po wysłaniu formularza',
          ]}
        />
        <ConversionType
          icon={Globe}
          title="Połączenie telefoniczne"
          description="Śledzenie kliknięć w numer telefonu"
          steps={[
            'W Google Ads: Cele > + Nowe > Połączenie telefoniczne',
            'Wybierz "Kliknięcia w numer telefonu na stronie"',
            'Zainstaluj tag śledzenia połączeń',
            'Opcjonalnie: użyj numeru przekierowującego Google',
          ]}
        />
      </Stack>
    </Box>
  );
}
